import { CurrentKycDtoStatus } from "@token/shared";
import type { CustomerDto } from "@token/shared";
import type { AuthProvider } from "@/lib/auth";
import { ref } from "vue";
import type { CustomerSession, CustomerSessionApi } from "./types";

export interface CreateCustomerSessionOptions {
  auth: AuthProvider;
  api: CustomerSessionApi;
}

export function createCustomerSession(opts: CreateCustomerSessionOptions): CustomerSession {
  const current = ref<CustomerDto | null>(null);
  const delegate = new DefaultCustomerSession(opts.auth, opts.api, (customer) => {
    current.value = customer;
  });

  return {
    get current() {
      return current.value;
    },
    sync: (options) => delegate.sync(options),
    clear: () => {
      delegate.clear();
      current.value = null;
    },
  };
}

class DefaultCustomerSession {
  private current: CustomerDto | null = null;
  private token: string | null = null;
  private pending: Promise<CustomerDto> | null = null;

  constructor(
    private readonly auth: AuthProvider,
    private readonly api: CustomerSessionApi,
    private readonly setCurrent: (customer: CustomerDto | null) => void,
  ) {}

  async sync(options: { force?: boolean } = {}): Promise<CustomerDto> {
    const token = this.auth.state.accessToken ?? this.auth.state.idToken;
    if (!token) {
      this.current = mockCustomerFromAuth(this.auth);
      this.setCurrent(this.current);
      this.token = null;
      return this.current;
    }

    if (!options.force && this.current && this.token === token) {
      return this.current;
    }

    if (!this.pending || options.force) {
      this.pending = this.api.syncAuthenticatedCustomer().then((customer) => {
        this.current = customer;
        this.setCurrent(customer);
        this.token = token;
        return customer;
      });
    }

    try {
      return await this.pending;
    } finally {
      this.pending = null;
    }
  }

  clear(): void {
    this.current = null;
    this.setCurrent(null);
    this.token = null;
    this.pending = null;
  }
}

function mockCustomerFromAuth(auth: AuthProvider): CustomerDto {
  const [firstName = "Portal", ...lastNameParts] = auth.state.user?.name.split(" ") ?? [];
  return {
    id: auth.state.user?.id ?? "mock-customer",
    externalId: auth.state.user?.id ?? "mock-external-id",
    firstName,
    lastName: lastNameParts.join(" ") || "User",
    email: auth.state.user?.email ?? "investor@example.com",
    kyc: {
      status: CurrentKycDtoStatus.NOT_STARTED,
    },
  };
}
